import React, { useMemo } from 'react';
import { DollarSign, Search, Calendar, Droplets, Sun, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

// Lazy load images only when needed - improves initial bundle size
const getLocalAsset = (serviceType: string): string | null => {
  switch (serviceType) {
    case 'sale':
    case 'sell':
      return '/assets/sell-helicopter-icon.png';
    case 'buy':
      return '/assets/buy-helicopters-icon.png';
    case 'rent':
      return '/assets/rent-helicopter-icon.png';
    case 'dry_lease':
      return '/assets/dry-lease-helicopter-icon.png';
    case 'wet_lease':
    case 'buy_wet_lease':
      return '/assets/wet-lease-helicopter-icon.png';
    case 'lease':
    case 'buy_dry_lease':
      return '/assets/lease-helicopter-icon.png';
    default:
      return null;
  }
};

interface ServiceIconProps {
  serviceType: string;
  className?: string;
  size?: number;
  context?: 'landing' | 'create-listing' | 'navigation';
  /** Scale factor for the icon artwork (1 = default, 1.3 = 30% larger) */
  artworkScale?: number;
}

const lucideIconMap: Record<string, typeof DollarSign> = {
  'sale': DollarSign,
  'sell': DollarSign,
  'buy': Search,
  'rent': Calendar,
  'dry_lease': Sun,
  'wet_lease': Droplets,
  'lease': Clock,
};

export const ServiceIcon: React.FC<ServiceIconProps> = ({ 
  serviceType, 
  className = "w-6 h-6 text-white",
  size = 24,
  context = 'landing',
  artworkScale = 1
}) => {
  // Scale transform for the icon artwork
  const scaleStyle = useMemo(() => 
    artworkScale !== 1 ? { transform: `scale(${artworkScale})` } : {},
    [artworkScale]
  );
  
  // Extract size from className if present (e.g., "w-40 h-40" -> don't use inline size)
  const hasClassNameSize = className.includes('w-') && className.includes('h-');
  
  // Get the appropriate local asset path (no import, uses public folder)
  const localAssetPath = getLocalAsset(serviceType);
  
  // Filter class based on context - navigation needs dark icons on white background
  const filterClass = context === 'navigation' ? "brightness-0 dark:invert" : "brightness-0 invert";
  
  // Priority: 1. Local assets (public folder), 2. Lucide fallback
  if (localAssetPath) {
    return (
      <img 
        src={localAssetPath}
        alt={`${serviceType} service icon`}
        className={cn("object-contain", filterClass, className)}
        loading="lazy"
        style={{ 
          ...(hasClassNameSize ? {} : { width: size, height: size }),
          ...scaleStyle
        }}
      />
    );
  }
  
  // Fall back to Lucide icon
  const LucideIcon = lucideIconMap[serviceType] || DollarSign;
  
  return <LucideIcon className={className} size={hasClassNameSize ? undefined : size} />;
};